import {
  GQBiChartKind,
  GQBiFilterOperator,
  GQBiQueryFilter,
  GQBiQueryMode,
  GQBiSavedViewVisibility,
  GQDimensionComputedType,
  GQDrilldownAggregateKind,
  GQDrilldownInclusionKind,
  GQDrilldownTimeSeriesKind,
  GQElectricityType,
  GQFilterConjunction,
  GQFilterExpressionPrimitiveWithNewFiltersInput,
  GQFilterFieldWatershed,
} from '../generated/graphql';
import {
  BiDynamicNormalizedMeasure,
  BiQueryComponentHashKey,
  BiQueryFilter,
  BiSavedViewQueryContextType,
  BiSavedViewType,
} from './types';
import assertNever from '../utils/assertNever';
import { DrilldownSavedViewType } from '../drilldown/types';
import invariant from 'invariant';
import { isBiStringValueNonPseudoNull } from './utils';
import { ALL_DEFAULT_TAGS } from '../footprintTagging/constants';
import { IntensityDenominatorKind } from '../utils/intensityDenominators';
import { YMInterval } from '../utils/YearMonth';
import { FootprintKind } from '../utils/constants';
import { SAVED_VIEW_DRAFT_ID } from '../footprint/constants';
import nullifyEmptyArray from '../utils/nullifyEmptyArray';
import { BaseFilterExpression } from '../drilldown/filterExpressionSchema';

export const CURRENT_SAVED_VIEW_SCHEMA_VERSION = '20240215';

type BiQueryParams = {
  measures?: Array<BiDynamicNormalizedMeasure> | null;
  filters?: Array<GQBiQueryFilter> | null;
  dimensions?: Array<string> | null;
  footprintKind?: string | null;
  searchTerm?: string | null;
  timeIntervals?: Array<YMInterval> | null;
  view?: string | null;
  chartKind?: GQBiChartKind | null;
};
export function translateDrilldownParamsToBiStringifiedParams({
  params,
}: {
  params: {
    biSavedViewId?: string;
    aggregateKind?: GQDrilldownAggregateKind;
    groupBy?: Array<string>;
    drilldownFilters?: Array<GQFilterExpressionPrimitiveWithNewFiltersInput>;
    customFields: Array<string>;
    intensityDenominatorName?: IntensityDenominatorKind;
    timeSeriesKind?: GQDrilldownTimeSeriesKind;
    searchTerm?: string;
    footprintKind?: string;
    fiscalYearStartMonth: number | null;
    interval?: YMInterval;
    chartKind?: GQBiChartKind;
  };
}): { [key: string]: string | undefined | null } {
  const paramsMaybeWithDefaults = params.biSavedViewId
    ? params
    : {
        // Legacy drilldown defaults
        groupBy: [
          GQFilterFieldWatershed.GhgScope,
          GQFilterFieldWatershed.GhgCategoryId,
        ],
        aggregateKind: GQDrilldownAggregateKind.Emissions,
        biSavedViewId: SAVED_VIEW_DRAFT_ID,
        timeSeriesKind: GQDrilldownTimeSeriesKind.Summary,
        footprintKind: FootprintKind.CorporateFootprint,
        chartKind: GQBiChartKind.TimeSeries,
        // Override with existing params
        ...params,
      };

  const {
    biSavedViewId,
    aggregateKind,
    groupBy,
    drilldownFilters,
    customFields,
    intensityDenominatorName,
    timeSeriesKind,
    searchTerm,
    footprintKind,
    fiscalYearStartMonth,
    interval,
    chartKind,
  } = paramsMaybeWithDefaults;

  const biQueryParams = {
    searchTerm: searchTerm ?? null,
    dimensions: translateGroupBy(
      groupBy ?? null,
      customFields,
      timeSeriesKind ?? null,
      fiscalYearStartMonth
    ),
    filters: drilldownFilters
      ? translateFilters(
          {
            conjunction: GQFilterConjunction.AndConjunction,
            expressions: drilldownFilters,
          },
          customFields,
          GQDrilldownInclusionKind.Net,
          aggregateKind
        )
      : null,
    measures: aggregateKind
      ? translateAggregateKind(aggregateKind, intensityDenominatorName ?? null)
      : null,
    footprintKind: footprintKind ?? null,
    view: biSavedViewId ?? null,
    timeIntervals: interval ? [interval] : null,
    chartKind: chartKind ?? null,
  };
  return makeBiQueryHashParams(biQueryParams);
}

export function makeBiQueryHashParams(params: BiQueryParams): {
  [key: string]: string | undefined | null;
} {
  return {
    [BiQueryComponentHashKey.footprintKind]: getStringifiedOrNull(
      params.footprintKind
    ),
    [BiQueryComponentHashKey.view]: getStringifiedOrNull(params.view),
    [BiQueryComponentHashKey.chartKind]: getStringifiedOrNull(params.chartKind),
    [BiQueryComponentHashKey.filters]: getStringifiedOrNull(params.filters),
    [BiQueryComponentHashKey.dimensions]: getStringifiedOrNull(
      params.dimensions
    ),
    [BiQueryComponentHashKey.measures]: getStringifiedOrNull(params.measures),
    [BiQueryComponentHashKey.intervals]: getStringifiedOrNull(
      params.timeIntervals
    ),
  };
}

export function getStringifiedOrNull(value: any | null): string | null {
  return value ? JSON.stringify(value) : null;
}

/**
 * Translates a DrilldownSavedViewType to a BiSavedViewType
 * We don't do anything with drilldown columns has_pie_chart, has_datatable_chart,
 * has_timeseries_chart, and description because they are unused.
 */
export function translateDrilldownSavedViewToBi(
  view: DrilldownSavedViewType,
  customFields: Array<string>,
  fiscalYearStartMonth: number | null
): Omit<BiSavedViewType, 'id' | 'stableId' | 'mode'> {
  return {
    name: view.name,
    orgId: view.orgId,
    ownerId: view.ownerId,
    editorId: view.ownerId,
    searchTerm: view.searchTerm,
    createdAt: view.createdAt ?? undefined,
    updatedAt: view.updatedAt ?? undefined,
    // Defaults
    jsonSchemaVersion: CURRENT_SAVED_VIEW_SCHEMA_VERSION,
    chartKind: GQBiChartKind.TimeSeries,
    lastVersionId: null,
    order: null,
    editVisibility: GQBiSavedViewVisibility.Org,
    viewVisibility: GQBiSavedViewVisibility.Org,
    // Translations
    timeIntervals: view.defaultTimeInterval ? [view.defaultTimeInterval] : [],
    dimensions:
      translateGroupBy(
        view.groupBy,
        customFields,
        view.timeSeriesKind,
        fiscalYearStartMonth
      ) ?? [],
    filters: view.filterExpressions
      ? translateFilters(
          view.filterExpressions,
          customFields,
          view.inclusionKind,
          view.aggregateKind
        )
      : [],
    normalizedMeasures: translateAggregateKind(
      view.aggregateKind,
      view.intensityDenominatorName
    ),
    queryContext: translateFootprintKind(view.footprintKind),
  };
}

/**
 * Translates the aggregate kind in the org_drilldown_view table and adds
 * it to a list of bi filters.
 */
function addAggregateKindFilters(
  filters: Array<BiQueryFilter>,
  aggregateKind: GQDrilldownAggregateKind
) {
  switch (aggregateKind) {
    case GQDrilldownAggregateKind.Electricity:
      return [
        ...filters,
        {
          dimension: 'energyKind',
          operator: GQBiFilterOperator.In,
          value: ['electricity'],
        },
        {
          dimension: 'electricityType',
          operator: GQBiFilterOperator.In,
          value: [GQElectricityType.Grid, GQElectricityType.Clean],
        },
      ];
    case GQDrilldownAggregateKind.LocationBasedEmissions:
    case GQDrilldownAggregateKind.PercentageEmissions:
    case GQDrilldownAggregateKind.PercentageOfCurrentView:
    case GQDrilldownAggregateKind.Emissions:
    case GQDrilldownAggregateKind.Intensity:
      return filters;
    default:
      assertNever(aggregateKind);
  }
}

/**
 * Translates the inclusion kind in the org_drilldown_view table and adds
 * it to a list of bi filters.
 */
function addInclusionKindFilters(
  filters: Array<BiQueryFilter>,
  inclusionKind: GQDrilldownInclusionKind
) {
  switch (inclusionKind) {
    case GQDrilldownInclusionKind.Gross:
      return [
        ...filters,
        {
          dimension: 'ghgCategoryId',
          operator: GQBiFilterOperator.NotIn,
          value: ['other_carbon_removal', 'other_additional_clean_power'],
        },
      ];
    case GQDrilldownInclusionKind.Removal:
      return [
        ...filters,
        {
          dimension: 'ghgCategoryId',
          operator: GQBiFilterOperator.In,
          value: ['other_carbon_removal', 'other_additional_clean_power'],
        },
      ];
    case GQDrilldownInclusionKind.Net:
      return filters;
    default:
      assertNever(inclusionKind);
  }
}

/**
 * Translates a filter expression group in the org_drilldown_view table
 * to a list of GQBiQueryFilter compatible with bi saved views. The
 * dimensions are tagged based on the org's custom tags.
 */
export function translateFilters(
  filterExpressionGroup: BaseFilterExpression,
  customFields: Array<string>,
  inclusionKind?: GQDrilldownInclusionKind,
  aggregateKind?: GQDrilldownAggregateKind
): Array<BiQueryFilter> {
  let filters: Array<BiQueryFilter> = filterExpressionGroup.expressions.map(
    (f) => {
      const operator =
        f.operator === 'in' ? GQBiFilterOperator.In : GQBiFilterOperator.NotIn;
      return {
        dimension: tagDimension(f.field, customFields),
        operator,
        value: f.value.map((value) =>
          // Convert pseudo-null values to null because that's what the data layer expects.
          // This includes NOT_SPECIFIED, 'null', '', undefined, null
          isBiStringValueNonPseudoNull(value) ? value : null
        ),
      };
    }
  );
  filters = inclusionKind
    ? addInclusionKindFilters(filters, inclusionKind)
    : filters;
  filters = aggregateKind
    ? addAggregateKindFilters(filters, aggregateKind)
    : filters;
  return filters;
}

/**
 * Translates they groupBys in the org_drilldown_view table
 * to a list of dimensions compatible with bi saved views. The
 * dimensions are tagged based on the org's custom tags.
 */
export function translateGroupBy(
  groupBys: Array<string> | null,
  customFields: Array<string>,
  timeSeriesKind: GQDrilldownTimeSeriesKind | null,
  fiscalYearStartMonth: number | null
): Array<string> | null {
  const dimensions = (groupBys ?? []).map((d) => tagDimension(d, customFields));
  const dimensionsWithTimeSeriesKind = timeSeriesKind
    ? translateTimeseriesKind(timeSeriesKind, dimensions, fiscalYearStartMonth)
    : dimensions;
  return nullifyEmptyArray(dimensionsWithTimeSeriesKind);
}

/**
 * Add tag prefix if dimensions exists in list of org's custom tags. AThis
 * ensures compatibility with bi.
 */
function tagDimension(dimension: string, customFields: Array<string>): string {
  // We exclude custom tags that are already default tags.
  const isDefaultTag = ALL_DEFAULT_TAGS.includes(dimension);
  return customFields.includes(dimension) && !isDefaultTag
    ? `tag:${dimension}`
    : dimension;
}

/**
 * Translates an aggregate kind in the org_drilldown_view table
 * to a list of BiDynamicNormalizedMeasure compatible with bi saved views.
 */
export function translateAggregateKind(
  aggregateKind: GQDrilldownAggregateKind,
  intensityDenominatorName: string | null
): Array<BiDynamicNormalizedMeasure> {
  switch (aggregateKind) {
    case GQDrilldownAggregateKind.Emissions:
      return [{ baseMeasure: 'tco2eQuantity', normalizingQuery: null }];
    case GQDrilldownAggregateKind.Electricity:
      return [{ baseMeasure: 'mwhConsumed', normalizingQuery: null }];
    case GQDrilldownAggregateKind.LocationBasedEmissions:
      return [{ baseMeasure: 'tco2eLocationQuantity', normalizingQuery: null }];
    case GQDrilldownAggregateKind.PercentageEmissions:
      return [
        {
          baseMeasure: 'tco2eQuantity',
          normalizingQuery: {
            measure: 'tco2eQuantity',
            dimensions: [],
            filters: [],
          },
        },
      ];
    case GQDrilldownAggregateKind.PercentageOfCurrentView:
      return [
        {
          baseMeasure: 'tco2eQuantity',
          normalizingQuery: {
            measure: 'tco2eQuantity',
            dimensions: [],
            computedFiltersType: GQDimensionComputedType.NumeratorIntersection,
          },
        },
      ];
    case GQDrilldownAggregateKind.Intensity:
      invariant(
        intensityDenominatorName,
        'Must have intensity denominator name if aggregate kind is Intensity.'
      );
      return [
        {
          baseMeasure: 'tco2eQuantity',
          normalizingQuery: {
            measure: intensityDenominatorName,
            computedDimensionType: 'NumeratorIntersection',
            filters: [],
          },
        },
      ];
    default:
      assertNever(aggregateKind);
  }
}

/**
 * Translates a footprint kind in the org_drilldown_view table
 * to the bi saved view snapshot query context object.
 */
export function translateFootprintKind(
  footprintKind: string
): BiSavedViewQueryContextType {
  return {
    mode: GQBiQueryMode.Snapshot,
    footprintKind,
  };
}

/**
 * Translates a timeseries kind in the org_drilldown_view table
 * to a timeseries dimension that we can pivot on in bi.
 */
function translateTimeseriesKind(
  timeseriesKind: GQDrilldownTimeSeriesKind,
  dimensions: Array<string>,
  fiscalYearStartMonth: number | null
): Array<string> {
  switch (timeseriesKind) {
    case GQDrilldownTimeSeriesKind.Monthly:
      return dimensions.includes('month')
        ? dimensions
        : ['month', ...dimensions];
    case GQDrilldownTimeSeriesKind.Yearly:
      const yearColumn = fiscalYearStartMonth ? 'fiscalYear' : 'calendarYear';
      return dimensions.includes(yearColumn)
        ? dimensions
        : [yearColumn, ...dimensions];
    case GQDrilldownTimeSeriesKind.Summary:
      return dimensions;
    default:
      assertNever(timeseriesKind);
  }
}
